import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Instagram, Facebook, Loader2, Check, Download, Users, Mail, Calendar, ArrowLeft, Lock, LogOut } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Asset URLs
const LOGO_URL = "https://customer-assets.emergentagent.com/job_candle-launch/artifacts/9cv667ua_Brixton-Candle-Co-Logo-Green-Yellow.bak.png";
const BRAND_IMAGE_URL = "https://customer-assets.emergentagent.com/job_353a5a30-30d0-46a6-8338-4fd3b0700d51/artifacts/sn723bqd_1Brixton-Candle-Co-homepage-Slider--150x85.png";
const HERO_IMAGE_URL = "https://customer-assets.emergentagent.com/job_candle-launch/artifacts/boyqivig_1Brixton-Candle-Co-homepage-Slider-.jpg";

// Auth Helper Functions
const getToken = () => localStorage.getItem("brixton_admin_token");
const setToken = (token) => localStorage.setItem("brixton_admin_token", token);
const removeToken = () => localStorage.removeItem("brixton_admin_token");

// Login Page Component
const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      if (response.data.success && response.data.token) {
        setToken(response.data.token);
        window.location.href = "/admin";
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid email or password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="login-page">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="Brixton Candle Co." 
            className="w-24 h-24 mx-auto mb-4"
          />
          <h1 className="font-heading text-2xl text-[#1A1A1A]">Admin Login</h1>
          <p className="font-body text-sm text-gray-500 mt-2">Brixton Candle Company</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className="block font-body text-sm text-gray-600 mb-2">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="h-12 rounded-none border-gray-300 focus:border-[#2E8B57] focus:ring-[#2E8B57]"
              data-testid="login-email"
              required
            />
          </div>
          
          <div>
            <label className="block font-body text-sm text-gray-600 mb-2">Password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="h-12 rounded-none border-gray-300 focus:border-[#2E8B57] focus:ring-[#2E8B57]"
              data-testid="login-password"
              required
            />
          </div>

          {error && (
            <div className="text-red-500 text-sm font-body flex items-center gap-2" data-testid="login-error">
              <Lock className="w-4 h-4" />
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full h-12 bg-[#2E8B57] hover:bg-[#257a4a] text-white font-body rounded-none border-b-2 border-[#D4A017]"
            data-testid="login-submit"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              "Sign In"
            )}
          </Button>
        </form>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <a 
            href="/" 
            className="font-body text-sm text-[#2E8B57] hover:text-[#257a4a] transition-colors"
          >
            ← Back to Coming Soon page
          </a>
        </div>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    const verifyAuth = async () => {
      const token = getToken();
      if (!token) {
        setIsAuthenticated(false);
        return;
      }

      try {
        await axios.get(`${API}/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAuthenticated(true);
      } catch (err) {
        removeToken();
        setIsAuthenticated(false);
      }
    };

    verifyAuth();
  }, []);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2E8B57]" />
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const ComingSoonPage = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !email.includes("@")) {
      setSubmitStatus("error");
      setMessage("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/subscribe`, { email });
      setSubmitStatus("success");
      setMessage(response.data.message);
      setEmail("");
    } catch (error) {
      setSubmitStatus("error");
      if (error.response?.data?.detail) {
        setMessage(error.response.data.detail);
      } else {
        setMessage("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="brixton-container" data-testid="coming-soon-page">
      <div className="min-h-screen grid grid-cols-1 md:grid-cols-12">
        {/* Content Section - Left Side on Desktop */}
        <div className="md:col-span-5 flex flex-col justify-center px-8 py-12 md:px-12 lg:px-16 order-2 md:order-1">
          {/* Logo */}
          <div className="mb-10 md:mb-12 mobile-logo-center md:block animate-fade-in-up">
            <img
              src={LOGO_URL}
              alt="Brixton Candle Co. Logo"
              className="w-24 h-24 md:w-28 md:h-28"
              data-testid="company-logo" />

          </div>

          {/* Headline */}
          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] tracking-tight leading-[1.1] mb-6 animate-fade-in-up-delay-1"
            data-testid="main-headline">

            Something New Is{" "}
            <span className="text-[#2E8B57]">Brewing…</span>
          </h1>

          {/* Subtext */}
          <div className="space-y-4 mb-10 animate-fade-in-up-delay-2">
            <p className="font-body text-base md:text-lg leading-relaxed text-gray-600">
              We're currently behind the scenes pouring new ideas, testing fresh scents, 
              and getting things ready for what's next at Brixton Candle Company.
            </p>
            <p className="font-body text-base md:text-lg leading-relaxed text-gray-600">Our shop will be back online soon with some exciting new fragrances we can't wait for you to experience.


            </p>
            <p className="font-body text-base md:text-lg leading-relaxed text-gray-600">
              Thank you for your patience while we perfect the next collection.
            </p>
          </div>

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="mb-8 animate-fade-in-up-delay-3">
            <div className="flex flex-col sm:flex-row gap-4">
              <Input
                type="email"
                placeholder="Enter your email for early access..."
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="email-input-brixton h-12 flex-1 text-base font-body"
                disabled={isSubmitting}
                data-testid="email-input" />

              <Button
                type="submit"
                disabled={isSubmitting}
                className="submit-btn-brixton h-12 px-8 text-lg"
                data-testid="submit-button">

                {isSubmitting ?
                <Loader2 className="w-5 h-5 animate-spin" /> :

                "Notify Me"
                }
              </Button>
            </div>

            {/* Status Message */}
            {submitStatus &&
            <div
              className={`mt-4 flex items-center gap-2 success-message ${
              submitStatus === "success" ? "text-[#2E8B57]" : "text-red-500"}`
              }
              data-testid="status-message">

                {submitStatus === "success" && <Check className="w-5 h-5" />}
                <span className="font-body text-sm">{message}</span>
              </div>
            }
          </form>

          {/* Optional Small Text */}
          <p className="font-body text-sm text-gray-500 tracking-wide uppercase mb-8 animate-fade-in-up-delay-3">
            Launching new scents soon.
          </p>

          {/* Social Links */}
          <div className="flex items-center gap-6 animate-fade-in-up-delay-3">
            <a
              href="https://www.facebook.com/brixtoncandleco"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              data-testid="facebook-link"
              aria-label="Facebook">

              <Facebook className="w-6 h-6" />
            </a>
            <a
              href="https://www.instagram.com/brixton_candleco/"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              data-testid="instagram-link"
              aria-label="Instagram">

              <Instagram className="w-6 h-6" />
            </a>
          </div>
        </div>

        {/* Image Section - Right Side on Desktop */}
        <div className="md:col-span-7 hero-image-container h-[40vh] md:h-screen order-1 md:order-2 relative bg-[#F9FAF9] flex items-center justify-center">
          <img
            src={HERO_IMAGE_URL}
            alt="Artisanal Candles"
            className="w-full h-full object-contain"
            data-testid="hero-image" />
        </div>
      </div>
    </div>);

};

// Admin Dashboard Component
const AdminDashboard = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      setIsLoading(true);
      const token = getToken();
      const response = await axios.get(`${API}/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(response.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 401) {
        removeToken();
        window.location.href = "/login";
        return;
      }
      setError("Failed to load subscribers");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    removeToken();
    window.location.href = "/login";
  };

  const exportToCSV = () => {
    if (subscribers.length === 0) return;
    
    const headers = ["Email", "Subscribed Date"];
    const csvContent = [
      headers.join(","),
      ...subscribers.map(sub => {
        const date = new Date(sub.subscribed_at).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        });
        return `${sub.email},${date}`;
      })
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `brixton-subscribers-${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="admin-dashboard">
      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/" className="text-[#2E8B57] hover:text-[#257a4a] transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </a>
              <div>
                <h1 className="font-heading text-2xl text-[#1A1A1A]">Subscriber Dashboard</h1>
                <p className="font-body text-sm text-gray-500">Brixton Candle Company</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={handleLogout}
                variant="outline"
                className="font-body text-sm text-gray-600 hover:text-[#1A1A1A] border-gray-300 rounded-none"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
              <img 
                src={LOGO_URL} 
                alt="Brixton Candle Co." 
                className="w-12 h-12"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-[#F9FAF9] p-6 rounded-lg border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-[#2E8B57]" />
              <span className="font-body text-sm text-gray-500 uppercase tracking-wide">Total Subscribers</span>
            </div>
            <p className="font-heading text-4xl text-[#1A1A1A]" data-testid="total-count">
              {isLoading ? "..." : subscribers.length}
            </p>
          </div>
          
          <div className="bg-[#F9FAF9] p-6 rounded-lg border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <Mail className="w-5 h-5 text-[#2E8B57]" />
              <span className="font-body text-sm text-gray-500 uppercase tracking-wide">Latest Signup</span>
            </div>
            <p className="font-body text-lg text-[#1A1A1A]" data-testid="latest-signup">
              {isLoading ? "..." : subscribers.length > 0 
                ? formatDate(subscribers[subscribers.length - 1]?.subscribed_at)
                : "No subscribers yet"}
            </p>
          </div>

          <div className="bg-[#F9FAF9] p-6 rounded-lg border border-gray-100 flex items-center justify-center">
            <Button
              onClick={exportToCSV}
              disabled={subscribers.length === 0 || isLoading}
              className="bg-[#2E8B57] hover:bg-[#257a4a] text-white font-body h-12 px-6 rounded-none border-b-2 border-[#D4A017]"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Subscribers Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-[#F9FAF9]">
            <h2 className="font-heading text-lg text-[#1A1A1A]">Email List</h2>
          </div>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#2E8B57]" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500 font-body">{error}</p>
              <Button 
                onClick={fetchSubscribers}
                className="mt-4 bg-[#2E8B57] hover:bg-[#257a4a] text-white"
              >
                Try Again
              </Button>
            </div>
          ) : subscribers.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="font-body text-gray-500">No subscribers yet</p>
              <p className="font-body text-sm text-gray-400 mt-1">Share your coming soon page to start collecting emails</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="subscribers-table">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left px-6 py-3 font-body text-sm font-medium text-gray-500 uppercase tracking-wide">#</th>
                    <th className="text-left px-6 py-3 font-body text-sm font-medium text-gray-500 uppercase tracking-wide">Email</th>
                    <th className="text-left px-6 py-3 font-body text-sm font-medium text-gray-500 uppercase tracking-wide">Subscribed</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((sub, index) => (
                    <tr 
                      key={sub.id} 
                      className="border-b border-gray-100 hover:bg-[#F9FAF9] transition-colors"
                      data-testid={`subscriber-row-${index}`}
                    >
                      <td className="px-6 py-4 font-body text-sm text-gray-400">{index + 1}</td>
                      <td className="px-6 py-4 font-body text-[#1A1A1A]">{sub.email}</td>
                      <td className="px-6 py-4 font-body text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {formatDate(sub.subscribed_at)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<ComingSoonPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
    </div>);

}

export default App;